import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, Grid, Paper, Stack,
  MenuItem, Alert, Chip, Divider, LinearProgress, Avatar,
  Accordion, AccordionSummary, AccordionDetails, Tab, Tabs,
  CircularProgress, Tooltip, IconButton, Checkbox, FormControlLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useMutation, useQuery } from '@apollo/client';
import { ME_QUERY, UPDATE_PROFILE_MUTATION } from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import FamilyContactsCard from '../components/FamilyContactsCard';
import VerificationCard from '../components/VerificationCard';
import { EventCityField } from '../components/LocationFilter';
import { calcRasiNakshatra } from '../utils/vedicAstro';
import { useLovMany } from '../hooks/useLov';

// UTC offsets for the place of birth (most families: India)
const UTC_OFFSETS = [
  { value: '+05:30', label: 'India / Sri Lanka (UTC+05:30)' },
  { value: '+05:45', label: 'Nepal (UTC+05:45)' },
  { value: '+06:00', label: 'Bangladesh (UTC+06:00)' },
  { value: '+08:00', label: 'Singapore / Malaysia (UTC+08:00)' },
  { value: '+04:00', label: 'UAE / Oman (UTC+04:00)' },
  { value: '+03:00', label: 'Saudi Arabia / Qatar / Kuwait (UTC+03:00)' },
  { value: '+00:00', label: 'UK — winter (UTC+00:00)' },
  { value: '+01:00', label: 'UK — summer (UTC+01:00)' },
  { value: '+10:00', label: 'Australia — Sydney (UTC+10:00)' },
  { value: '+12:00', label: 'New Zealand (UTC+12:00)' },
  { value: '-04:00', label: 'USA — Eastern summer (UTC−04:00)' },
  { value: '-05:00', label: 'USA — Eastern winter (UTC−05:00)' },
  { value: '-07:00', label: 'USA — Pacific summer (UTC−07:00)' },
  { value: '-08:00', label: 'USA — Pacific winter (UTC−08:00)' },
];

const sel = (label, name, value, onChange, options, loading) => (
  <TextField label={label} name={name} value={value || ''} onChange={onChange} fullWidth select size="small"
    disabled={loading} InputProps={{ endAdornment: loading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null }}>
    {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
  </TextField>
);
const txt = (label, name, value, onChange, extra = {}) => (
  <TextField label={label} name={name} value={value || ''} onChange={onChange} fullWidth size="small" {...extra} />
);

const LOV_CATS = ['profile_for','gender','mother_tongue','religion','community',
                  'marital_status','physical_status','employment_type',
                  'annual_income','education','horoscope_star','horoscope_rasi',
                  'country','state','city'];

export default function ProfilePage() {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState(0);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    profileFor: 'myself', firstName: '', lastName: '', gender: 'male', dob: '',
    motherTongue: '', religion: 'Hindu', community: '', caste: '', subCaste: '',
    heightCm: '', maritalStatus: 'never_married', physicalStatus: 'normal',
    education: '', profession: '', employmentType: '', annualIncome: '',
    country: 'India', state: '', city: '', pincode: '',
    aboutMe: '', familyBackground: '',
    horoscopeStar: '', horoscopeRasi: '', horoscopeGothram: '',
    birthTime: '', birthPlace: '', birthUtcOffset: '+05:30', horoscopeUrl: '',
  });
  // Star/Rasi follow the calculation until the user picks them by hand
  const [horoscopeManual, setHoroscopeManual] = useState(false);
  const horoscopeFileRef = useRef(null);

  const { lovMap, loading: lovLoading } = useLovMany(LOV_CATS);

  const { data } = useQuery(ME_QUERY, {
    onCompleted: (d) => {
      if (d?.me?.profile) {
        const p = d.me.profile;
        setForm({
          profileFor:       p.profileFor       || 'myself',
          firstName:        p.firstName        || '',
          lastName:         p.lastName         || '',
          gender:           p.gender           || 'male',
          dob:              p.dob              || '',
          motherTongue:     p.motherTongue     || '',
          religion:         p.religion         || 'Hindu',
          community:        p.community        || '',
          caste:            p.caste            || '',
          subCaste:         p.subCaste         || '',
          heightCm:         p.heightCm         || '',
          maritalStatus:    p.maritalStatus    || 'never_married',
          physicalStatus:   p.physicalStatus   || 'normal',
          education:        p.education        || '',
          profession:       p.profession       || '',
          employmentType:   p.employmentType   || '',
          annualIncome:     p.annualIncome      || '',
          country:          p.country           || 'India',
          state:            p.state             || '',
          city:             p.city              || '',
          pincode:          p.pincode           || '',
          aboutMe:          p.aboutMe          || '',
          familyBackground: p.familyBackground || '',
          horoscopeStar:    p.horoscopeStar    || '',
          horoscopeRasi:    p.horoscopeRasi    || '',
          horoscopeGothram: p.horoscopeGothram || '',
          birthTime:        p.birthTime        || '',
          birthPlace:       p.birthPlace       || '',
          birthUtcOffset:   p.birthUtcOffset   || '+05:30',
          horoscopeUrl:     p.horoscopeUrl     || '',
        });
      } else {
        setEditing(true);
      }
    },
  });

  const [saveProfile, { loading }] = useMutation(UPDATE_PROFILE_MUTATION);
  const ch = (e) => {
    if (['horoscopeStar', 'horoscopeRasi'].includes(e.target.name)) setHoroscopeManual(true);
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  // Rasi & Nakshatra from the date, time and place (time zone) of birth
  const horoscopeCalc = calcRasiNakshatra(form.dob, form.birthTime, form.birthUtcOffset);
  const lovValueFor = (cat, name) => (lovMap[cat] || []).find(o => o.value.toLowerCase() === name.toLowerCase())?.value;
  const calcStar = horoscopeCalc ? lovValueFor('horoscope_star', horoscopeCalc.nakshatra) : null;
  const calcRasi = horoscopeCalc ? lovValueFor('horoscope_rasi', horoscopeCalc.rasi) : null;
  const applyCalc = () => {
    setForm(f => ({ ...f, horoscopeStar: calcStar || f.horoscopeStar, horoscopeRasi: calcRasi || f.horoscopeRasi }));
    setHoroscopeManual(false);
  };
  // While editing, fill them in as the birth details are entered (unless picked by hand)
  useEffect(() => {
    if (!editing || horoscopeManual || !form.birthTime || !calcStar || !calcRasi) return;
    setForm(f => (f.horoscopeStar === calcStar && f.horoscopeRasi === calcRasi ? f : { ...f, horoscopeStar: calcStar, horoscopeRasi: calcRasi }));
  }, [editing, horoscopeManual, form.birthTime, calcStar, calcRasi]);
  const calcDiffers = horoscopeCalc && (form.horoscopeStar !== calcStar || form.horoscopeRasi !== calcRasi);

  // Helper: find label for a stored value in a LOV category
  const lovLabel = (cat, val) => {
    if (!val) return '\u2014';
    const found = (lovMap[cat] || []).find(o => o.value === val);
    return found ? found.label : val;
  };

  // First save: declare the profile is for matrimony and the information is true (MeitY advisory)
  const [declared, setDeclared] = useState(false);
  const needsDeclaration = !data?.me?.profile?.matrimonyDeclaredAt;

  const handleSave = async () => {
    setError('');
    try {
      await saveProfile({
        variables: {
          input: { userId: user.id, ...form, heightCm: form.heightCm ? parseInt(form.heightCm) : null,
            country: form.country || null, state: form.state || null, city: form.city || null,
            pincode: form.pincode || null,
            birthTime: form.birthTime || null, horoscopeUrl: form.horoscopeUrl || null,
            ...(needsDeclaration && { matrimonyDeclaration: declared }) }
        },
        refetchQueries: [{ query: ME_QUERY }],
      });
      setSuccess('Profile saved successfully!');
      setEditing(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save profile.');
    }
  };

  const profile    = data?.me?.profile;
  const completion = profile?.profileCompletion || 0;
  const initials   = profile
    ? `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`
    : user?.email?.[0]?.toUpperCase();

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3} spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, fontSize: '1.5rem' }}>
            {initials}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {profile ? `${profile.firstName} ${profile.lastName}` : 'My Profile'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
              {data?.me?.role && (
                <Chip label={data.me.role.displayName} color="primary" size="small" />
              )}
            </Stack>
          </Box>
        </Stack>
        {!editing && profile && (
          <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setEditing(true)}>
            Edit Profile
          </Button>
        )}
      </Stack>

      {/* Bride/groom families: primary and secondary contacts (all communications go to both) */}
      {['bride_family', 'groom_family'].includes(data?.me?.role?.name) && (
        <Box id="contacts"><FamilyContactsCard sx={{ mb: 3 }} /></Box>
      )}
      {['bride_family', 'groom_family'].includes(data?.me?.role?.name) && (
        <Box id="verification"><VerificationCard sx={{ mb: 3 }} /></Box>
      )}

      {/* Completion bar */}
      <Paper elevation={1} sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" mb={1}>
          <Typography variant="body2" fontWeight={700}>Profile Completion</Typography>
          <Typography variant="body2" fontWeight={700}
            color={completion >= 80 ? 'success.main' : 'warning.main'}>
            {completion}%
          </Typography>
        </Stack>
        <LinearProgress variant="determinate" value={completion}
          color={completion >= 80 ? 'success' : completion >= 50 ? 'warning' : 'error'}
          sx={{ height: 8, borderRadius: 4 }} />
        {completion < 80 && (
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            Complete your profile to appear in more matches. Add horoscope, income and about me.
          </Typography>
        )}
      </Paper>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

      {!editing && profile ? (
        /* View mode */
        <Box>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab label="Personal" />
            <Tab label="Professional" />
            <Tab label="Horoscope" />
            <Tab label="About" />
          </Tabs>

          {tab === 0 && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Grid container spacing={2.5}>
                {[
                  { l: 'Profile For',    v: lovLabel('profile_for', form.profileFor) },
                  { l: 'Full Name',      v: `${form.firstName} ${form.lastName}` },
                  { l: 'Gender',         v: lovLabel('gender', form.gender) },
                  { l: 'Date of Birth',  v: form.dob },
                  { l: 'Religion',       v: lovLabel('religion', form.religion) },
                  { l: 'Mother Tongue',  v: lovLabel('mother_tongue', form.motherTongue) },
                  { l: 'Community',      v: lovLabel('community', form.community) },
                  { l: 'Height',         v: form.heightCm ? `${form.heightCm} cm` : null },
                  { l: 'Marital Status', v: lovLabel('marital_status', form.maritalStatus) },
                  { l: 'Country',        v: lovLabel('country', form.country) },
                  { l: 'State',          v: lovLabel('state', form.state) },
                  { l: 'City',           v: lovLabel('city', form.city) },
                  { l: 'Pincode',        v: form.pincode },
                ].map(f => (
                  <Grid item xs={12} sm={6} key={f.l}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{f.l}</Typography>
                    <Typography variant="body1" fontWeight={500}>{f.v || '\u2014'}</Typography>
                    <Divider sx={{ mt: 1 }} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {tab === 1 && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Grid container spacing={2.5}>
                {[
                  { l: 'Education',       v: lovLabel('education', form.education) },
                  { l: 'Profession',      v: form.profession },
                  { l: 'Employment Type', v: lovLabel('employment_type', form.employmentType) },
                  { l: 'Annual Income',   v: lovLabel('annual_income', form.annualIncome) },
                ].map(f => (
                  <Grid item xs={12} sm={6} key={f.l}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{f.l}</Typography>
                    <Typography variant="body1" fontWeight={500}>{f.v || '\u2014'}</Typography>
                    <Divider sx={{ mt: 1 }} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {tab === 2 && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Grid container spacing={2.5}>
                {[
                  { l: 'Date of Birth',    v: form.dob },
                  { l: 'Birth Time',       v: form.birthTime || '—' },
                  { l: 'Place of Birth',   v: form.birthPlace ? `${form.birthPlace}${form.birthUtcOffset ? ` (UTC${form.birthUtcOffset})` : ''}` : '—' },
                  { l: 'Star (Nakshatra)', v: lovLabel('horoscope_star', form.horoscopeStar) },
                  { l: 'Rasi',             v: lovLabel('horoscope_rasi', form.horoscopeRasi) },
                  { l: 'Gothram',          v: form.horoscopeGothram },
                ].map(f => (
                  <Grid item xs={12} sm={6} key={f.l}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{f.l}</Typography>
                    <Typography variant="body1" fontWeight={500}>{f.v || '\u2014'}</Typography>
                    <Divider sx={{ mt: 1 }} />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">Horoscope Document</Typography>
                  {form.horoscopeUrl ? (
                    <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                      <InsertDriveFileIcon color="primary" fontSize="small" />
                      <Typography variant="body2" fontWeight={500}>Horoscope uploaded</Typography>
                      <Tooltip title="Open document">
                        <IconButton size="small" href={form.horoscopeUrl} target="_blank" rel="noopener noreferrer">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.disabled" mt={0.5}>No horoscope document uploaded</Typography>
                  )}
                  <Divider sx={{ mt: 1 }} />
                </Grid>
              </Grid>
            </Paper>
          )}

          {tab === 3 && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              {form.aboutMe && (
                <>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>About Me</Typography>
                  <Typography variant="body1" color="text.secondary" lineHeight={1.8} mb={3}>{form.aboutMe}</Typography>
                  <Divider sx={{ mb: 3 }} />
                </>
              )}
              {form.familyBackground && (
                <>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Family Background</Typography>
                  <Typography variant="body1" color="text.secondary" lineHeight={1.8}>{form.familyBackground}</Typography>
                </>
              )}
              {!form.aboutMe && !form.familyBackground && (
                <Typography color="text.secondary">No description yet. Click Edit Profile to add details.</Typography>
              )}
            </Paper>
          )}
        </Box>
      ) : (
        /* Edit mode */
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>

          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>Personal Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>{sel('Profile For','profileFor',form.profileFor,ch,lovMap['profile_for']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{sel('Gender','gender',form.gender,ch,lovMap['gender']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{txt('First Name','firstName',form.firstName,ch,{required:true})}</Grid>
                <Grid item xs={12} sm={6}>{txt('Last Name','lastName',form.lastName,ch,{required:true})}</Grid>
                <Grid item xs={12} sm={6}>{txt('Date of Birth','dob',form.dob,ch,{type:'date',InputLabelProps:{shrink:true}})}</Grid>
                <Grid item xs={12} sm={6}>{txt('Height (cm)','heightCm',form.heightCm,ch,{type:'number',inputProps:{min:100,max:250}})}</Grid>
                <Grid item xs={12} sm={6}>{sel('Marital Status','maritalStatus',form.maritalStatus,ch,lovMap['marital_status']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{sel('Country','country',form.country,ch,lovMap['country']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>
                  {sel('State / UT','state',form.state,
                    (e) => { ch(e); setForm(f => ({ ...f, city: '' })); },
                    lovMap['state']||[],lovLoading)}
                </Grid>
                <Grid item xs={12} sm={6}>{sel('City','city',form.city,ch,lovMap['city']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{txt('Pincode','pincode',form.pincode,ch,{inputProps:{maxLength:10}})}</Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Divider />

          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>Religion &amp; Community</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>{sel('Religion','religion',form.religion,ch,lovMap['religion']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{sel('Mother Tongue','motherTongue',form.motherTongue,ch,lovMap['mother_tongue']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{sel('Community / Caste','community',form.community,ch,lovMap['community']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{txt('Sub Caste','subCaste',form.subCaste,ch)}</Grid>
                <Grid item xs={12} sm={6}>{txt('Gothram','horoscopeGothram',form.horoscopeGothram,ch)}</Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Divider />

          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>Horoscope Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                {/* Date (Personal Details), time and place of birth define the Rasi & Nakshatra */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Time of Birth" name="birthTime" type="time" value={form.birthTime || ''} onChange={ch}
                    fullWidth size="small" InputLabelProps={{ shrink: true }}
                    helperText="Local time at the place of birth"
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <EventCityField
                    label="Place of Birth" size="small" value={form.birthPlace}
                    onChange={(v) => setForm(f => ({ ...f, birthPlace: v }))}
                    helperText="Town or city where you were born"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select label="Time zone" name="birthUtcOffset" value={form.birthUtcOffset || '+05:30'} onChange={ch}
                    fullWidth size="small" helperText="Of the place of birth"
                  >
                    {UTC_OFFSETS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  {!form.dob ? (
                    <Alert severity="info" sx={{ py: 0.5 }}>Enter the Date of Birth (Personal Details), time and place of birth to work out the Rasi and Nakshatra.</Alert>
                  ) : horoscopeCalc && (
                    <Alert
                      severity={horoscopeCalc.nearBoundary || !horoscopeCalc.timeKnown ? 'warning' : 'success'}
                      icon={<AutoFixHighIcon />} sx={{ py: 0.5 }}
                      action={calcDiffers && <Button color="inherit" size="small" onClick={applyCalc}>Use these</Button>}
                    >
                      Rasi <b>{horoscopeCalc.rasi}</b> · Nakshatra <b>{horoscopeCalc.nakshatra}</b>, pada {horoscopeCalc.pada}
                      {' '}(Moon at {horoscopeCalc.siderealLongitude.toFixed(2)}° sidereal, Lahiri).
                      {!horoscopeCalc.timeKnown && ' Add the time of birth — without it noon is assumed, and the Nakshatra may differ.'}
                      {horoscopeCalc.timeKnown && horoscopeCalc.nearBoundary && ' The Moon was close to the next Nakshatra at this time — please confirm with your horoscope.'}
                      {horoscopeCalc.timeKnown && !horoscopeCalc.nearBoundary && !calcDiffers && ' Filled in below.'}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>{sel('Star (Nakshatra)','horoscopeStar',form.horoscopeStar,ch,lovMap['horoscope_star']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{sel('Rasi','horoscopeRasi',form.horoscopeRasi,ch,lovMap['horoscope_rasi']||[],lovLoading)}</Grid>
                {/* Horoscope document upload */}
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2, mt: 0.5 }} />
                  <Typography variant="body2" fontWeight={600} mb={1}>Horoscope Document</Typography>
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
                    <input
                      ref={horoscopeFileRef}
                      type="file"
                      accept="application/pdf,image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        // Create a local object URL as a placeholder;
                        // replace with a real upload endpoint in production
                        const localUrl = URL.createObjectURL(file);
                        setForm(f => ({ ...f, horoscopeUrl: localUrl }));
                        e.target.value = '';
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      size="small"
                      onClick={() => horoscopeFileRef.current?.click()}
                    >
                      {form.horoscopeUrl ? 'Replace Horoscope' : 'Upload Horoscope (PDF / Image)'}
                    </Button>
                    {form.horoscopeUrl && (
                      <>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <InsertDriveFileIcon color="success" fontSize="small" />
                          <Typography variant="body2" color="success.main">File selected</Typography>
                          <Tooltip title="Preview">
                            <IconButton size="small" href={form.horoscopeUrl} target="_blank" rel="noopener noreferrer">
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Button size="small" color="error" onClick={() => setForm(f => ({ ...f, horoscopeUrl: '' }))}>
                          Remove
                        </Button>
                      </>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                    Upload a PDF or image of your horoscope chart for matching.
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Divider />

          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>Education &amp; Career</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>{sel('Highest Education','education',form.education,ch,lovMap['education']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{txt('Profession / Job Title','profession',form.profession,ch)}</Grid>
                <Grid item xs={12} sm={6}>{sel('Employment Type','employmentType',form.employmentType,ch,lovMap['employment_type']||[],lovLoading)}</Grid>
                <Grid item xs={12} sm={6}>{sel('Annual Income','annualIncome',form.annualIncome,ch,lovMap['annual_income']||[],lovLoading)}</Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Divider />

          <Accordion disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>About Me &amp; Family</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>{txt('About Me','aboutMe',form.aboutMe,ch,{multiline:true,rows:3,helperText:'What would you like matches to know about you?'})}</Grid>
                <Grid item xs={12}>{txt('Family Background','familyBackground',form.familyBackground,ch,{multiline:true,rows:3,helperText:'Family values, traditions and background'})}</Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 2 }} />

          {needsDeclaration && (
            <FormControlLabel
              sx={{ mb: 2, alignItems: 'flex-start' }}
              control={<Checkbox checked={declared} onChange={(e) => setDeclared(e.target.checked)} sx={{ pt: 0.25 }} />}
              label={
                <Typography variant="body2">
                  I declare that this profile is created to find a matrimonial alliance (not for dating), that I have the consent of the person
                  it is for, and that the information is true to the best of my knowledge. The bride is at least 18 and the groom at least 21.
                </Typography>
              }
            />
          )}

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {profile && (
              <Button variant="outlined" onClick={() => setEditing(false)}>Cancel</Button>
            )}
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={loading || (needsDeclaration && !declared)}>
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
